<!-- eslint-disable vue/no-v-html -->
<template>
  <section
    aria-labelledby="caption"
    class="dynamic-table"
    :class="`dynamic-table--${theme}`"
  >
    <span
      id="caption"
      class="dynamic-table__caption"
    >
      <slot name="title" />
      <slot name="subtitle" />
    </span>
    <div class="dynamic-table__wrapper">
      <div
        ref="scrollContainer"
        class="dynamic-table__scroll"
        @scroll="updateScrollState"
      >
        <table class="dynamic-table__container">
          <thead class="dynamic-table__head">
            <tr class="dynamic-table__row">
              <th
                v-for="(header, index) in headersToRender"
                :key="'header-' + index"
                class="dynamic-table__header"
              >
                <slot
                  name="header"
                  :header="header"
                  :index="index"
                >
                  <p v-html="sanitize(header)" />
                </slot>
              </th>
            </tr>
          </thead>

          <tbody class="dynamic-table__body">
            <tr
              v-for="(row, rowIndex) in rowsToRender"
              :key="'row-' + rowIndex"
              class="dynamic-table__row"
            >
              <td
                v-for="(cell, colIndex) in row"
                :key="'cell-' + rowIndex + '-' + colIndex"
                class="dynamic-table__data"
              >
                <slot
                  name="cell"
                  :cell="cell"
                  :row="rowIndex"
                  :col="colIndex"
                >
                  <p v-html="sanitize(cell)" />
                </slot>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="dynamic-table__disclaimer">
        <slot name="disclaimer" />
      </div>

      <div class="dynamic-table__controls">
        <button
          class="dynamic-table__arrow"
          :disabled="scrollPosition === 0"
          :aria-label="leftButtonLabel"
          @click="scrollLeft"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            aria-hidden="true"
            fill="none"
            stroke="#EDF2F5"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              stroke="none"
              d="M0 0h24v24H0z"
              fill="none"
            />
            <path d="M5 12l14 0" />
            <path d="M5 12l6 6" />
            <path d="M5 12l6 -6" />
          </svg>
        </button>

        <button
          class="dynamic-table__arrow"
          :disabled="isAtEnd"
          :aria-label="rightButtonLabel"
          @click="scrollRight"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            aria-hidden="true"
            fill="none"
            stroke="#EDF2F5"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path
              stroke="none"
              d="M0 0h24v24H0z"
              fill="none"
            />
            <path d="M5 12l14 0" />
            <path d="M13 18l6 -6" />
            <path d="M13 6l6 6" />
          </svg>
        </button>
      </div>
    </div>
  </section>
</template>

<script>
import DOMPurify from 'dompurify'

export default {
  name: 'dynamic-table',
  props: {
    headers: {
      type: Array,
      default: () => [],
    },
    data: {
      type: Array,
      default: () => [],
    },
    scrollStep: {
      type: Number,
      default: 200,
    },
    leftButtonLabel: {
      type: String,
      default: 'Scroll the table to the left',
    },
    rightButtonLabel: {
      type: String,
      default: 'Scroll the table to the right',
    },
    theme: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'light-mode'].includes(value),
    },
  },
  data() {
    return {
      scrollPosition: 0,
      isAtEnd: false,
    }
  },
  computed: {
    headersToRender() {
      return this.headers.length
        ? this.headers
        : Array.from({ length: 3 }, (_, i) => `Coluna ${i + 1}`)
    },
    rowsToRender() {
      return this.data.length
        ? this.data
        : Array.from({ length: 3 }, () => Array.from({ length: this.headersToRender.length }, () => ''))
    },
  },
  mounted() {
    this.updateScrollState()
    window.addEventListener('resize', this.updateScrollState)
  },
  beforeUnmount() {
    window.removeEventListener('resize', this.updateScrollState)
  },
  methods: {
    scrollLeft() {
      const container = this.$refs.scrollContainer
      container.scrollBy({ left: -this.scrollStep, behavior: 'smooth' })
      setTimeout(this.updateScrollState, 300)
    },
    scrollRight() {
      const container = this.$refs.scrollContainer
      container.scrollBy({ left: this.scrollStep, behavior: 'smooth' })
      setTimeout(this.updateScrollState, 300)
    },
    updateScrollState() {
      const container = this.$refs.scrollContainer
      if (!container) return
      this.scrollPosition = container.scrollLeft
      this.isAtEnd = container.scrollWidth - container.clientWidth - container.scrollLeft
        <= 5
    },
    sanitize(html) {
      return DOMPurify.sanitize(html)
    },
  },
}
</script>

<style lang="scss" scoped>
.dynamic-table {
  display: flex;
  flex-direction: column;
  gap: 1rem;

  &__caption {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }

  &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
  }

  &__scroll {
    width: 100%;
    overflow-x: hidden;
    scroll-behavior: smooth;
  }

  &__container {
    border-collapse: collapse;
    width: 100%;
  }

  &__header {
    font-weight: 700;
    border-right: 0.0625rem solid $blue-06;
    border-left: 0.0625rem solid $blue-06;
    color: $blue-06;
    background: $blue-03;
  }

  &__header,
  &__data {
    padding: 1rem;
    text-align: center;
  }

  &__header {
    white-space: nowrap;
  }

  &__data {
    font-weight: 300;
    color: #3b3b3b;
    border-bottom: 0.0625rem solid #43515a33;
    white-space: break-spaces;
  }

  &__disclaimer {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 1rem;
    letter-spacing: 0.0625rem;
  }

  &__controls {
    display: flex;
    justify-content: center;
    gap: 1rem;

    @include screen-size('md') {
      align-self: flex-end;
    }
  }

  &__arrow {
    background: $blue-03;
    border-radius: 0.25rem;
    border: 0.0625rem solid $blue-03;
    padding: 0.5rem;
    cursor: pointer;
    transition: all 0.5s;

    &:hover:not(:disabled) {
      background: transparent;

      svg {
        stroke: $blue-03;
      }
    }

    &:disabled {
      opacity: 0.1;
      cursor: not-allowed;
    }
  }

  &--light-mode {
    .dynamic-table__header {
      background: $neutral-00;
      color: $blue-06;
    }

    .dynamic-table__arrow {
      border-color: $neutral-00;
      background: $neutral-00;

      svg {
        stroke: $blue-06;
      }

      &:hover:not(:disabled) {
        svg {
          stroke: $neutral-00;
        }
      }
    }
  }
}
</style>
